import { http } from "./client";

/** Where a month's pay is, from the employee's point of view - in order. */
export type EarningsStage =
  | "pending_attendance"
  | "attendance_confirmed"
  | "estimate_updated"
  | "payroll_processing"
  | "payroll_approved"
  | "payroll_paid";

export type EarningsDayState = "confirmed" | "pending" | "upcoming";

export interface EarningsDay {
  date: string;
  state: EarningsDayState;
  day_type: "working" | "weekly_off" | "holiday";
  attendance_status?: string;
  first_in?: string;
  last_out?: string;
  worked_minutes: number;
  late?: boolean;
  base_earnings: number;
  lop_adjustment: number;
  overtime: number;
  total: number;
  /** Whether total is part of the earnings so far. */
  counted: boolean;
  note?: string;
}

/** Today's pay while the employee is still checked in: what payroll gives for
 * today if they checked out at as_of. Payroll pays by thresholds, so it rises
 * in steps; next_* say when the next step comes. */
export interface EarningsLive {
  active: boolean;
  checked_in_at?: string;
  as_of: string;
  worked_minutes: number;
  status: string;
  payable_units: number;
  /** What payroll pays for today if they check out now (the exact step). */
  amount: number;
  /** Today's pay accrued smoothly by the time worked: meets payroll's amount at
   * the half-day and full-day marks, ahead of it in between. */
  accrued_amount: number;
  /** How fast accrued_amount is rising now, per minute (0 once a full day). */
  accrued_per_minute: number;
  /** The month's confirmed earnings plus accrued_amount. */
  earned_accruing: number;
  next_status?: "half_day" | "full_day";
  minutes_to_next?: number;
  next_amount?: number;
  half_day_minutes: number;
  full_day_minutes: number;
}

export interface EarningsLine {
  code: string;
  name: string;
  amount: number;
  basis?: string;
}

export interface EarningsWidgetSettings {
  enabled: boolean;
  show_extras: boolean;
  show_daily_breakdown: boolean;
  /** "first", "25", "50", "75", "100" to the company's own message. */
  milestone_messages: Record<string, string>;
  /** Day of the month salary is paid; 0 = not configured, 31 = the last day. */
  payday_day: number;
  /** The payday in November pays October (rather than November). */
  payday_covers_prev_month: boolean;
  achievement_punctuality: boolean;
  achievement_consistency: boolean;
  achievement_goals: boolean;
  punctuality_min_days: number;
  recognition_enabled: boolean;
  updated_at?: string;
}

/** An employee's month on the My Earnings card. Every amount is an estimate of
 * gross pay before statutory deductions, worked out by the payroll engine from
 * settled attendance; `payroll` carries the approved figures once payroll ran. */
export interface Earnings {
  employee_id: string;
  employee_code: string;
  employee_name: string;
  period: string;
  period_label: string;
  as_of: string;
  enabled: boolean;
  settings?: EarningsWidgetSettings;
  calculated: boolean;
  reason?: string;
  salary_type?: string;
  salary_basis?: string;
  basis_days?: number;
  monthly_salary: number;
  per_day_salary: number;
  earned_to_date: number;
  /** earned_to_date plus today's live pay (equal when nothing is live). */
  earned_including_live: number;
  /** earned_to_date plus today's smoothly accruing pay (equal when nothing is live). */
  earned_accruing: number;
  live?: EarningsLive;
  base_earned_to_date: number;
  today_earnings: number;
  today_state: EarningsDayState | "";
  extras_total: number;
  estimated_month_end: number;
  extras: EarningsLine[];
  planned_working_days: number;
  completed_working_days: number;
  remaining_working_days: number;
  present_days: number;
  paid_leave_days: number;
  lop_days: number;
  pending_days: number;
  progress_percent: number;
  milestones: { percent: number; reached: boolean; message: string }[];
  message?: string;
  stage: EarningsStage;
  payroll?: { run_status: string; finalized: boolean; payslip_id?: string; gross_earnings?: number; net_pay?: number };
  days: EarningsDay[];
  notes?: string[];
}

export const earningsApi = {
  mine: (period?: string) => http.get<Earnings>("/employee/self/earnings", period ? { period } : undefined),
  forEmployee: (employeeId: string, period?: string) =>
    http.get<Earnings>(`/payroll/earnings/${employeeId}`, period ? { period } : undefined),
  settings: () => http.get<EarningsWidgetSettings>("/payroll/earnings-settings"),
  saveSettings: (s: EarningsWidgetSettings) => http.put<EarningsWidgetSettings>("/payroll/earnings-settings", s),
};

export interface Payday {
  configured: boolean;
  date?: string;
  days_until: number;
  /** The salary month this payday pays. */
  period?: string;
  period_label?: string;
  stage?: EarningsStage;
  /** True until payroll for that month is approved: forecast is an estimate. */
  estimated: boolean;
  forecast?: number;
  net_pay?: number;
  payslip_id?: string;
  payslip_available: boolean;
}

export interface AttendanceProgress {
  label: string;
  working_days: number;
  attended: number;
  late: number;
  remaining: number;
  percent: number;
}

export interface AchievementItem {
  id: string;
  achievement: string;
  title: string;
  description: string;
  period: string;
  /** What the achievement was for, e.g. "September 2026" or "Week of 28 Sep". */
  period_label?: string;
  awarded_at: string;
  /** True until the dashboard has shown the celebration once. */
  new: boolean;
}

export interface Achievements {
  enabled: boolean;
  week: AttendanceProgress;
  month: AttendanceProgress;
  items: AchievementItem[];
  rules: { name: string; rule: string }[];
}

export interface Recognition {
  id: string;
  from_employee_id: string;
  to_employee_id: string;
  message: string;
  created_at: string;
  from_name?: string;
  to_name?: string;
}

export interface RecognitionView {
  enabled: boolean;
  received: Recognition[];
  given: Recognition[];
  /** Direct reports a manager can appreciate; empty for everyone else. */
  team: { employee_id: string; employee_code: string; name: string }[];
}

export interface WalletPart {
  period: string;
  period_label: string;
  amount: number;
  settled: number;
  /** Today's pay so far while checked in (current month only). */
  live: number;
  /** True when amount is payroll's approved gross rather than an estimate. */
  approved: boolean;
  stage: string;
}

/** Earned and not yet paid through payroll: this month (with today's live pay)
 * plus an earlier month whose payroll is unpaid. Gross, and calculated, not stored. */
export interface Wallet {
  enabled: boolean;
  as_of: string;
  balance: number;
  current?: WalletPart;
  previous?: WalletPart;
  live_active: boolean;
  payday_configured: boolean;
  next_payday?: string;
  days_to_payday: number;
}

export const experienceApi = {
  wallet: () => http.get<Wallet>("/employee/self/wallet"),
  payday: () => http.get<Payday>("/employee/self/payday"),
  achievements: () => http.get<Achievements>("/employee/self/achievements"),
  markSeen: (ids: string[]) => http.post<{ ok: boolean }>("/employee/self/achievements/seen", { ids }),
  recognition: () => http.get<RecognitionView>("/employee/self/recognition"),
  recognise: (employeeId: string, message: string) =>
    http.post<Recognition>("/employee/self/recognition", { employee_id: employeeId, message }),
};
